import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Gift, Trash2, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border border-amber-200",
  converted: "bg-blue-50 text-blue-700 border border-blue-200",
  bonus_paid: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border border-rose-200",
};
const STATUS_LABEL = { pending: "In attesa", converted: "Convertito", bonus_paid: "Bonus pagato", rejected: "Rifiutato" };

export default function Referrals() {
  const { user } = useAuth();
  const isStaff = ["admin", "support"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ recommended_client_name: "", recommended_client_email: "", recommended_client_phone: "", notes: "" });

  const load = async () => {
    try { const { data } = await api.get("/referrals"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.recommended_client_name.trim()) { toast.error("Inserisci il nome del cliente"); return; }
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/referrals", payload); toast.success("Segnalazione creata");
      setForm({ recommended_client_name: "", recommended_client_email: "", recommended_client_phone: "", notes: "" });
      setAdding(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const patch = async (id, body) => {
    try { await api.patch(`/referrals/${id}`, body); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/referrals/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setBonus = (r) => {
    const amt = prompt("Importo bonus (EUR):", r.bonus_amount || "");
    if (amt === null) return;
    patch(r.id, { bonus_amount: Number(amt), order_id: r.order_id || undefined });
  };

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Gift size={24} className="text-blue-700" /> Segnalazioni & Bonus</h1>
          <p className="text-sm text-slate-500 mt-1">Programma di referral per le aziende partner</p>
        </div>
        {!adding && <Button onClick={() => setAdding(true)} className="bg-blue-900 hover:bg-blue-800" data-testid="referral-add-btn"><Plus size={16} className="mr-1.5" /> Nuova segnalazione</Button>}
      </div>

      {adding && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-3 max-w-2xl" data-testid="referral-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Cliente segnalato *</Label><Input value={form.recommended_client_name} onChange={(e) => setForm({ ...form, recommended_client_name: e.target.value })} data-testid="referral-client-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.recommended_client_email} onChange={(e) => setForm({ ...form, recommended_client_email: e.target.value })} data-testid="referral-client-email" /></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.recommended_client_phone} onChange={(e) => setForm({ ...form, recommended_client_phone: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex gap-2"><Button onClick={create} className="bg-blue-900 hover:bg-blue-800" data-testid="referral-save-btn">Salva</Button><Button variant="ghost" onClick={() => setAdding(false)}>Annulla</Button></div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
        {items.length === 0 && <p className="text-sm text-slate-400 p-8 text-center">Nessuna segnalazione.</p>}
        {items.map((r) => (
          <div key={r.id} className="flex items-center gap-4 p-4 flex-wrap" data-testid="referral-row">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-800">{r.recommended_client_name}</div>
              <div className="text-xs text-slate-400">{r.recommended_client_email || "—"} · {formatDate(r.created_at)}</div>
              {r.notes && <div className="text-xs text-slate-500 mt-0.5">{r.notes}</div>}
            </div>
            <div className="text-sm text-slate-600">{r.bonus_amount ? money(r.bonus_amount) : "—"}</div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || ""}`} data-testid="referral-status">{STATUS_LABEL[r.status] || r.status}</span>
            {isStaff && (
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => setBonus(r)} data-testid="referral-bonus-btn">Bonus</Button>
                {!r.bonus_paid && <Button size="sm" variant="outline" onClick={() => patch(r.id, { bonus_paid: true })} data-testid="referral-pay-btn"><Check size={14} className="mr-1" /> Paga</Button>}
                <button onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600 p-1.5"><Trash2 size={15} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
